import { useCallback, useEffect, useState } from "react";
import {
  AddSongToListInput,
  ChordList,
  ChordListItem,
  CreateChordListInput,
  SongSavedInListInfo,
  UpdateChordListInput,
  UpdateChordListItemInput,
} from "../Entities/ChordListModel";
import { SongModel } from "../Entities/SongModel";
import { useUser } from "./useUser";

const API_BASE = process.env.REACT_APP_API_URL || "";
const GUEST_LISTS_STORAGE_KEY = "chordmaster_guest_lists_v1";

const DEFAULT_GUEST_LISTS: ChordList[] = [
  {
    id: 1,
    user_id: "guest",
    name: "To Learn",
    description: "Songs I want to practice next",
    color: "#3b82f6",
    position: 0,
    items: [],
    item_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 2,
    user_id: "guest",
    name: "Practicing",
    description: "Currently practicing chords and rhythm",
    color: "#f59e0b",
    position: 1,
    items: [],
    item_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: 3,
    user_id: "guest",
    name: "Mastered",
    description: "Songs I can play smoothly",
    color: "#10b981",
    position: 2,
    items: [],
    item_count: 0,
    created_at: new Date().toISOString(),
    updated_at: new Date().toISOString(),
  },
];

/* =====================================
   Guest LocalStorage Helpers
===================================== */

export function loadGuestListsFromStorage(): ChordList[] {
  try {
    const raw = localStorage.getItem(GUEST_LISTS_STORAGE_KEY);
    if (!raw) {
      saveGuestListsToStorage(DEFAULT_GUEST_LISTS);
      return DEFAULT_GUEST_LISTS;
    }
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed) && parsed.length > 0) {
      return parsed.map(mapChordList);
    }
    saveGuestListsToStorage(DEFAULT_GUEST_LISTS);
    return DEFAULT_GUEST_LISTS;
  } catch {
    return DEFAULT_GUEST_LISTS;
  }
}

export function saveGuestListsToStorage(lists: ChordList[]): void {
  try {
    localStorage.setItem(GUEST_LISTS_STORAGE_KEY, JSON.stringify(lists));
  } catch {
    // ignore
  }
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^|;\\s*)(" + name + ")=([^;]*)"));
  return match ? decodeURIComponent(match[3]) : null;
}

function getHeaders(userId?: string): HeadersInit {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (userId) {
    headers["X-User-Id"] = userId;
  }
  const csrf = getCookie("chordmaster_csrf");
  if (csrf) {
    headers["X-CSRF-Token"] = csrf;
  }
  return headers;
}

function mapSongInItem(item: any): ChordListItem {
  return {
    ...item,
    song: item.song ? new SongModel(item.song) : null,
  };
}

function mapChordList(list: any): ChordList {
  return {
    ...list,
    items: (list.items || []).map(mapSongInItem),
    item_count: (list.items || []).length,
  };
}

/* =====================================
   REST API Calls & Caching
===================================== */

let cachedUserLists: {
  userIdKey: string;
  timestamp: number;
  data: ChordList[];
} | null = null;

const inFlightUserLists = new Map<string, Promise<ChordList[]>>();
const LISTS_CACHE_TTL_MS = 15_000;

export function invalidateChordListsCache(): void {
  cachedUserLists = null;
  inFlightUserLists.clear();
}

if (typeof window !== "undefined") {
  window.addEventListener("chordmaster_lists_changed", () => invalidateChordListsCache());
}

export async function apiFetchUserLists(userId?: string, forceRefresh = false): Promise<ChordList[]> {
  const cacheKey = userId || "default";
  const now = Date.now();

  if (!forceRefresh && cachedUserLists && cachedUserLists.userIdKey === cacheKey && (now - cachedUserLists.timestamp < LISTS_CACHE_TTL_MS)) {
    return cachedUserLists.data;
  }

  const existing = inFlightUserLists.get(cacheKey);
  if (existing && !forceRefresh) {
    return existing;
  }

  const fetchPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/api/lists`, {
        headers: getHeaders(userId),
        credentials: "include",
      });
      if (!res.ok) {
        throw new Error(`Failed to fetch chord lists: ${res.statusText}`);
      }
      const data = await res.json();
      const mapped = (data || []).map(mapChordList);
      cachedUserLists = {
        userIdKey: cacheKey,
        timestamp: Date.now(),
        data: mapped,
      };
      return mapped;
    } finally {
      inFlightUserLists.delete(cacheKey);
    }
  })();

  inFlightUserLists.set(cacheKey, fetchPromise);
  return fetchPromise;
}

export async function apiCreateChordList(
  input: CreateChordListInput,
  userId?: string
): Promise<ChordList> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists`, {
    method: "POST",
    headers: getHeaders(userId),
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Failed to create chord list: ${res.statusText}`);
  }
  const data = await res.json();
  return mapChordList(data);
}

export async function apiUpdateChordList(
  listId: number,
  input: UpdateChordListInput,
  userId?: string
): Promise<ChordList> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists/${listId}`, {
    method: "PUT",
    headers: getHeaders(userId),
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Failed to update chord list: ${res.statusText}`);
  }
  const data = await res.json();
  return mapChordList(data);
}

export async function apiDeleteChordList(
  listId: number,
  userId?: string
): Promise<void> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists/${listId}`, {
    method: "DELETE",
    headers: getHeaders(userId),
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`Failed to delete chord list: ${res.statusText}`);
  }
}

export async function apiReorderChordLists(
  listIds: number[],
  userId?: string
): Promise<void> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists/reorder`, {
    method: "POST",
    headers: getHeaders(userId),
    credentials: "include",
    body: JSON.stringify({ list_ids: listIds }),
  });
  if (!res.ok) {
    throw new Error(`Failed to reorder chord lists: ${res.statusText}`);
  }
}

export async function apiAddSongToList(
  listId: number,
  input: AddSongToListInput,
  userId?: string
): Promise<ChordListItem> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists/${listId}/items`, {
    method: "POST",
    headers: getHeaders(userId),
    credentials: "include",
    body: JSON.stringify({ song_id: input.song_id, notes: input.notes, position: input.position }),
  });
  if (!res.ok) {
    if (res.status === 404) throw new Error("The target list was deleted. Choose another list.");
    const error = await res.json().catch(() => ({}));
    throw new Error(error.detail || `Failed to add song to list (${res.status}).`);
  }
  const data = await res.json();
  return mapSongInItem(data);
}

export async function apiUpdateListItem(
  itemId: number,
  input: UpdateChordListItemInput,
  userId?: string
): Promise<ChordListItem> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists/items/${itemId}`, {
    method: "PUT",
    headers: getHeaders(userId),
    credentials: "include",
    body: JSON.stringify(input),
  });
  if (!res.ok) {
    throw new Error(`Failed to update list item: ${res.statusText}`);
  }
  const data = await res.json();
  return mapSongInItem(data);
}

export async function apiDeleteListItem(
  itemId: number,
  userId?: string
): Promise<void> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists/items/${itemId}`, {
    method: "DELETE",
    headers: getHeaders(userId),
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`Failed to delete list item: ${res.statusText}`);
  }
}

export async function apiMoveListItem(
  itemId: number,
  targetListId: number,
  position: number,
  userId?: string
): Promise<ChordListItem> {
  invalidateChordListsCache();
  const res = await fetch(
    `${API_BASE}/api/lists/items/move?item_id=${itemId}`,
    {
      method: "POST",
      headers: getHeaders(userId),
      credentials: "include",
      body: JSON.stringify({
        target_list_id: targetListId,
        position: position,
      }),
    }
  );
  if (!res.ok) {
    throw new Error(`Failed to move list item: ${res.statusText}`);
  }
  const data = await res.json();
  return mapSongInItem(data);
}

export async function apiBatchReorderItems(
  items: { id: number; list_id: number; position: number }[],
  userId?: string
): Promise<void> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists/items/reorder-batch`, {
    method: "POST",
    headers: getHeaders(userId),
    credentials: "include",
    body: JSON.stringify({ items }),
  });
  if (!res.ok) {
    throw new Error(`Failed to batch reorder items: ${res.statusText}`);
  }
}

export async function apiGetSongSavedStatus(
  songId: number,
  userId?: string
): Promise<SongSavedInListInfo[]> {
  const res = await fetch(`${API_BASE}/api/lists/song/${songId}/saved`, {
    headers: getHeaders(userId),
    credentials: "include",
  });
  if (!res.ok) {
    return [];
  }
  return await res.json();
}

export async function apiExportChordLists(
  userId?: string
): Promise<Record<string, Array<{ url: string; notes: string | null }>>> {
  const res = await fetch(`${API_BASE}/api/lists/export`, {
    headers: getHeaders(userId),
    credentials: "include",
  });
  if (!res.ok) {
    throw new Error(`Failed to export lists: ${res.statusText}`);
  }
  return await res.json();
}

export async function apiImportChordLists(
  payload: any,
  userId?: string
): Promise<{ lists_imported: number; items_imported: number; warnings: string[]; message: string }> {
  invalidateChordListsCache();
  const res = await fetch(`${API_BASE}/api/lists/import`, {
    method: "POST",
    headers: getHeaders(userId),
    credentials: "include",
    body: JSON.stringify(payload),
  });
  if (!res.ok) {
    throw new Error(`Failed to import lists: ${res.statusText}`);
  }
  return await res.json();
}

export function triggerJsonDownload(data: any, filename: string): void {
  const jsonStr = typeof data === "string" ? data : JSON.stringify(data, null, 2);
  const blob = new Blob([jsonStr], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/* =====================================
   Lightweight Direct Operations (No Hook Mounting)
===================================== */

export function getGuestSongSavedStatus(songId: number): SongSavedInListInfo[] {
  const guestLists = loadGuestListsFromStorage();
  const saved: SongSavedInListInfo[] = [];
  guestLists.forEach((l) => {
    const item = l.items.find((i) => i.song_id === songId);
    if (item) {
      saved.push({
        list_id: l.id,
        list_name: l.name,
        item_id: item.id,
        notes: item.notes,
      });
    }
  });
  return saved;
}

export async function fetchSongSavedStatus(
  songId: number,
  userId?: string | null
): Promise<SongSavedInListInfo[]> {
  if (!userId) {
    return getGuestSongSavedStatus(songId);
  }
  return await apiGetSongSavedStatus(songId, userId);
}

export async function addSongToListDirect(
  listId: number,
  input: AddSongToListInput,
  userId?: string | null
): Promise<ChordListItem> {
  if (!userId) {
    const guestLists = loadGuestListsFromStorage();
    let createdOrUpdatedItem: ChordListItem | null = null;
    if (!guestLists.some((list) => list.id === listId)) {
      throw new Error("The target list was deleted. Choose another list.");
    }

    // Enforce single-list constraint: remove song from all other lists
    const cleanedLists = guestLists.map((l) => {
      if (l.id !== listId) {
        const items = l.items.filter((i) => i.song_id !== input.song_id);
        return { ...l, items, item_count: items.length };
      }
      return l;
    });

    const updated = cleanedLists.map((l) => {
      if (l.id === listId) {
        const existingIndex = l.items.findIndex((i) => i.song_id === input.song_id);
        if (existingIndex >= 0) {
          const current = l.items[existingIndex];
          createdOrUpdatedItem = {
            ...current,
            song: input.song || current.song,
            notes: input.notes !== undefined ? input.notes : current.notes,
            position: input.position !== undefined ? input.position : current.position,
            updated_at: new Date().toISOString(),
          };
          const items = [...l.items];
          items[existingIndex] = createdOrUpdatedItem;
          return { ...l, items, item_count: items.length };
        } else {
          const allItemIds = guestLists.flatMap((gl) => gl.items.map((it) => it.id));
          const nextItemId = allItemIds.reduce((max, id) => Math.max(max, id), 0) + 1;
          createdOrUpdatedItem = {
            id: nextItemId,
            list_id: listId,
            song_id: input.song_id,
            song: input.song,
            notes: input.notes,
            position: input.position ?? l.items.length,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          const items = [...l.items, createdOrUpdatedItem];
          return { ...l, items, item_count: items.length };
        }
      }
      return l;
    });

    saveGuestListsToStorage(updated);
    return createdOrUpdatedItem!;
  }

  const newItem = await apiAddSongToList(listId, input, userId);
  return newItem;
}

/* =====================================
   useChordLists Hook
===================================== */

export function useChordLists() {
  const { user, isAuthenticated, loading: userLoading } = useUser();
  const [lists, setLists] = useState<ChordList[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const activeUserId = user ? user.id : null;

  const refreshLists = useCallback(async () => {
    // If user is still loading, wait
    if (userLoading) return;

    // For logged-out / guest users, operate offline in localStorage
    if (!isAuthenticated || !activeUserId) {
      setLoading(true);
      setError(null);
      const guestLists = loadGuestListsFromStorage();
      setLists(guestLists);
      setLoading(false);
      return;
    }

    // For authenticated users, fetch from PostgreSQL backend
    try {
      setLoading(true);
      setError(null);
      const data = await apiFetchUserLists(activeUserId);
      setLists(data);
    } catch (err: any) {
      console.error("Error loading chord lists from backend:", err);
      // Fallback to guest storage if backend fails, avoiding harsh error crashes
      const guestLists = loadGuestListsFromStorage();
      setLists(guestLists);
      setError(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, activeUserId, userLoading]);

  useEffect(() => {
    refreshLists();
    window.addEventListener("chordmaster_lists_changed", refreshLists);
    return () => window.removeEventListener("chordmaster_lists_changed", refreshLists);
  }, [refreshLists]);

  const createList = useCallback(
    async (input: CreateChordListInput): Promise<ChordList> => {
      if (!isAuthenticated || !activeUserId) {
        const guestLists = loadGuestListsFromStorage();
        const nextId = guestLists.reduce((max, l) => Math.max(max, l.id), 0) + 1;
        const newList: ChordList = {
          id: nextId,
          user_id: "guest",
          name: input.name.trim(),
          description: input.description?.trim() || undefined,
          color: input.color || "#3b82f6",
          position: input.position ?? guestLists.length,
          items: [],
          item_count: 0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
        const updated = [...guestLists, newList];
        saveGuestListsToStorage(updated);
        setLists(updated);
        return newList;
      }

      const newList = await apiCreateChordList(input, activeUserId);
      setLists((prev) => [...prev, newList]);
      return newList;
    },
    [isAuthenticated, activeUserId]
  );

  const updateList = useCallback(
    async (listId: number, input: UpdateChordListInput): Promise<ChordList> => {
      if (!isAuthenticated || !activeUserId) {
        const guestLists = loadGuestListsFromStorage();
        let updatedList: ChordList | null = null;
        const updated = guestLists.map((l) => {
          if (l.id === listId) {
            updatedList = {
              ...l,
              name: input.name !== undefined ? input.name.trim() : l.name,
              description: input.description !== undefined ? input.description?.trim() || undefined : l.description,
              color: input.color !== undefined ? input.color : l.color,
              position: input.position !== undefined ? input.position : l.position,
              updated_at: new Date().toISOString(),
            };
            return updatedList;
          }
          return l;
        });
        saveGuestListsToStorage(updated);
        setLists(updated);
        return updatedList || guestLists[0];
      }

      const updated = await apiUpdateChordList(listId, input, activeUserId);
      setLists((prev) =>
        prev.map((l) => (l.id === listId ? { ...l, ...updated } : l))
      );
      return updated;
    },
    [isAuthenticated, activeUserId]
  );

  const deleteList = useCallback(
    async (listId: number): Promise<void> => {
      if (!isAuthenticated || !activeUserId) {
        const guestLists = loadGuestListsFromStorage();
        const updated = guestLists.filter((l) => l.id !== listId);
        saveGuestListsToStorage(updated);
        setLists(updated);
        return;
      }

      await apiDeleteChordList(listId, activeUserId);
      setLists((prev) => prev.filter((l) => l.id !== listId));
    },
    [isAuthenticated, activeUserId]
  );

  const reorderLists = useCallback(
    async (reorderedListIds: number[]): Promise<void> => {
      setLists((prev) => {
        const map = new Map(prev.map((l) => [l.id, l]));
        const next = reorderedListIds
          .map((id, index) => {
            const list = map.get(id);
            return list ? { ...list, position: index } : null;
          })
          .filter((l): l is ChordList => l !== null);
        if (!isAuthenticated || !activeUserId) {
          saveGuestListsToStorage(next);
        }
        return next;
      });

      if (isAuthenticated && activeUserId) {
        await apiReorderChordLists(reorderedListIds, activeUserId);
      }
    },
    [isAuthenticated, activeUserId]
  );

  const addSongToList = useCallback(
    async (listId: number, input: AddSongToListInput): Promise<ChordListItem> => {
      const activeId = isAuthenticated && activeUserId ? activeUserId : null;
      const newItem = await addSongToListDirect(listId, input, activeId);
      if (activeId) {
        await refreshLists();
      } else {
        setLists(loadGuestListsFromStorage());
      }
      return newItem;
    },
    [isAuthenticated, activeUserId, refreshLists]
  );

  const updateListItem = useCallback(
    async (
      itemId: number,
      input: UpdateChordListItemInput
    ): Promise<ChordListItem> => {
      if (!isAuthenticated || !activeUserId) {
        const guestLists = loadGuestListsFromStorage();
        let updatedItem: ChordListItem | null = null;

        const updated = guestLists.map((l) => {
          const item = l.items.find((i) => i.id === itemId);
          if (item) {
            updatedItem = {
              ...item,
              notes: input.notes !== undefined ? input.notes : item.notes,
              position: input.position !== undefined ? input.position : item.position,
              list_id: input.list_id !== undefined ? input.list_id : item.list_id,
              updated_at: new Date().toISOString(),
            };
            const items = l.items.map((i) => (i.id === itemId ? updatedItem! : i));
            return { ...l, items };
          }
          return l;
        });

        saveGuestListsToStorage(updated);
        setLists(updated);
        return updatedItem || ({} as ChordListItem);
      }

      const updatedItem = await apiUpdateListItem(itemId, input, activeUserId);
      setLists((prev) =>
        prev.map((l) => {
          if (l.id === updatedItem.list_id) {
            const items = l.items.map((i) =>
              i.id === itemId ? { ...i, ...updatedItem } : i
            );
            return { ...l, items };
          }
          // If moved from another list
          const hadItem = l.items.some((i) => i.id === itemId);
          if (hadItem && l.id !== updatedItem.list_id) {
            const items = l.items.filter((i) => i.id !== itemId);
            return { ...l, items, item_count: items.length };
          }
          return l;
        })
      );
      return updatedItem;
    },
    [isAuthenticated, activeUserId]
  );

  const deleteListItem = useCallback(
    async (itemId: number): Promise<void> => {
      if (!isAuthenticated || !activeUserId) {
        const guestLists = loadGuestListsFromStorage();
        const updated = guestLists.map((l) => {
          if (l.items.some((i) => i.id === itemId)) {
            const items = l.items.filter((i) => i.id !== itemId);
            return { ...l, items, item_count: items.length };
          }
          return l;
        });
        saveGuestListsToStorage(updated);
        setLists(updated);
        return;
      }

      await apiDeleteListItem(itemId, activeUserId);
      setLists((prev) =>
        prev.map((l) => {
          if (l.items.some((i) => i.id === itemId)) {
            const items = l.items.filter((i) => i.id !== itemId);
            return { ...l, items, item_count: items.length };
          }
          return l;
        })
      );
    },
    [isAuthenticated, activeUserId]
  );

  const moveListItem = useCallback(
    async (
      itemId: number,
      sourceListId: number,
      targetListId: number,
      newPosition: number
    ): Promise<void> => {
      let movedItem: ChordListItem | null = null;

      setLists((prev) => {
        const updated = prev.map((l) => {
          if (l.id === sourceListId) {
            const item = l.items.find((i) => i.id === itemId);
            if (item) movedItem = { ...item, list_id: targetListId };
            const items = l.items.filter((i) => i.id !== itemId);
            return { ...l, items, item_count: items.length };
          }
          return l;
        });

        if (!movedItem) return prev;

        const result = updated.map((l) => {
          if (l.id === targetListId) {
            const items = [...l.items];
            const targetPos = Math.max(0, Math.min(newPosition, items.length));
            items.splice(targetPos, 0, movedItem!);
            const reindexed = items.map((it, idx) => ({ ...it, position: idx }));
            return { ...l, items: reindexed, item_count: reindexed.length };
          }
          return l;
        });

        if (!isAuthenticated || !activeUserId) {
          saveGuestListsToStorage(result);
        }

        return result;
      });

      if (isAuthenticated && activeUserId) {
        await apiMoveListItem(itemId, targetListId, newPosition, activeUserId);
      }
    },
    [isAuthenticated, activeUserId]
  );

  const batchReorderItems = useCallback(
    async (
      reorderedItems: { id: number; list_id: number; position: number }[]
    ): Promise<void> => {
      if (!isAuthenticated || !activeUserId) {
        const guestLists = loadGuestListsFromStorage();
        const itemMap = new Map(reorderedItems.map((it) => [it.id, it]));
        const updated = guestLists.map((l) => {
          const items = l.items.map((i) => {
            const update = itemMap.get(i.id);
            if (update) {
              return { ...i, position: update.position, list_id: update.list_id };
            }
            return i;
          });
          items.sort((a, b) => a.position - b.position);
          return { ...l, items };
        });
        saveGuestListsToStorage(updated);
        setLists(updated);
        return;
      }

      await apiBatchReorderItems(reorderedItems, activeUserId);
    },
    [isAuthenticated, activeUserId]
  );

  const getSongSavedStatus = useCallback(
    async (songId: number): Promise<SongSavedInListInfo[]> => {
      return fetchSongSavedStatus(songId, isAuthenticated && activeUserId ? activeUserId : null);
    },
    [isAuthenticated, activeUserId]
  );

  const exportLists = useCallback(async (): Promise<void> => {
    let exportPayload: Record<string, Array<{ url: string; notes: string | null }>> = {};
    if (isAuthenticated && activeUserId) {
      exportPayload = await apiExportChordLists(activeUserId);
    } else {
      const guestLists = loadGuestListsFromStorage();
      for (const l of guestLists) {
        exportPayload[l.name || String(l.id)] = l.items
          .filter((it) => it.song && it.song.url)
          .map((it) => ({
            url: it.song!.url,
            notes: it.notes || null,
          }));
      }
    }
    const dateStr = new Date().toISOString().split("T")[0];
    triggerJsonDownload(exportPayload, `chordmaster-lists-${dateStr}.json`);
  }, [isAuthenticated, activeUserId]);

  const importLists = useCallback(
    async (
      jsonData: any
    ): Promise<{ lists_imported: number; items_imported: number; warnings: string[] }> => {
      let normalized: Record<string, Array<any>> = {};
      if (jsonData && typeof jsonData === "object") {
        if (Array.isArray(jsonData.lists)) {
          for (const l of jsonData.lists) {
            normalized[l.name || String(l.id)] = l.items || [];
          }
        } else {
          normalized = jsonData;
        }
      } else {
        throw new Error("Invalid JSON format: expected a mapping of lists to songs");
      }

      if (isAuthenticated && activeUserId) {
        const res = await apiImportChordLists(normalized, activeUserId);
        await refreshLists();
        return {
          lists_imported: res.lists_imported,
          items_imported: res.items_imported,
          warnings: res.warnings || [],
        };
      }

      // Guest mode import: append-only
      const currentGuestLists = loadGuestListsFromStorage();
      const existingMap = new Map<string, ChordList>(
        currentGuestLists.map((l) => [l.name.trim().toLowerCase(), l])
      );
      const existingById = new Map<string, ChordList>(
        currentGuestLists.map((l) => [String(l.id), l])
      );

      let nextItemId =
        currentGuestLists
          .flatMap((l) => l.items)
          .reduce((max, it) => Math.max(max, it.id), 0) + 1;

      const warnings: string[] = [];
      const processedListIds = new Set<number>();
      let itemsImportedCount = 0;

      for (const [listKey, rawItems] of Object.entries(normalized)) {
        if (!Array.isArray(rawItems)) continue;
        const cleanKey = listKey.trim();
        const targetList =
          existingById.get(cleanKey) || existingMap.get(cleanKey.toLowerCase());
        if (!targetList) {
          warnings.push(`List '${listKey}' not found, skipped`);
          continue;
        }

        processedListIds.add(targetList.id);

        for (const itemData of rawItems) {
          if (!itemData || typeof itemData !== "object") continue;
          const url = (itemData.url || "").trim();
          const songId = itemData.song_id;
          const notes = itemData.notes !== undefined ? itemData.notes : null;

          const existingItem = targetList.items.find(
            (it) => (url && it.song?.url === url) || (songId && it.song_id === songId)
          );
          if (existingItem) {
            if (notes !== null) {
              existingItem.notes = notes;
            }
            itemsImportedCount++;
          } else {
            // Enforce single-list constraint across guest lists: remove from other lists
            for (const otherList of currentGuestLists) {
              if (otherList.id !== targetList.id) {
                otherList.items = otherList.items.filter(
                  (it) => !((url && it.song?.url === url) || (songId && it.song_id === songId))
                );
                otherList.item_count = otherList.items.length;
              }
            }

            const nextPos = targetList.items.length;
            const newItemId = nextItemId++;
            const songObj =
              itemData.song ||
              (url
                ? new SongModel({
                    id: songId || newItemId + 10000,
                    title: url.split("/").filter(Boolean).pop() || "Imported Song",
                    url,
                  } as any)
                : null);

            const newItem: ChordListItem = {
              id: newItemId,
              list_id: targetList.id,
              song_id: songId || songObj?.id || newItemId + 10000,
              position: nextPos,
              notes,
              song: songObj,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString(),
            };
            targetList.items.push(newItem);
            targetList.item_count = targetList.items.length;
            itemsImportedCount++;
          }
        }
      }

      saveGuestListsToStorage(currentGuestLists);
      setLists([...currentGuestLists]);

      return {
        lists_imported: processedListIds.size,
        items_imported: itemsImportedCount,
        warnings,
      };
    },
    [isAuthenticated, activeUserId, refreshLists]
  );

  return {
    lists,
    setLists,
    loading,
    error,
    refreshLists,
    createList,
    updateList,
    deleteList,
    reorderLists,
    addSongToList,
    updateListItem,
    deleteListItem,
    moveListItem,
    batchReorderItems,
    getSongSavedStatus,
    exportLists,
    importLists,
  };
}
